import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "../lib/api";
import PullToRefresh from "../components/PullToRefresh";
import { CardListSkeleton } from "../components/Skeletons";
import ComplianceDocsPicker from "../components/ComplianceDocsPicker";
import ComplianceBadge from "../components/ComplianceBadge";
import { ClientWaitingPill } from "../components/notes/ClientWaiting";
import { useClientWaiting } from "../hooks/useClientWaiting";
import useComplianceBadges from "../hooks/useComplianceBadges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { FileDropZone } from "../components/FileDropZone";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import SortHeader from "../components/SortHeader";
import { AiBriefButton, AiSmartAssignButton } from "../components/AiFeatures";
import { Briefcase, Plus, MagnifyingGlass, MapPin, User, Clock, Wrench, CalendarBlank, List, CalendarDots, CaretUp, CaretDown, CaretLeft, CaretRight, Bookmark, Hand, CheckSquare, Square, ArrowsClockwise, ListChecks, Trash, UserPlus, CalendarCheck, UserSwitch, CheckCircle } from "@phosphor-icons/react";
import { SubJobChip } from "../components/jobs/SubJobProgress";
import SubJobsExpandRows from "../components/jobs/SubJobsExpandRows";
import ScopeOfWorksField from "../components/jobs/ScopeOfWorksField";
import ClientFilterSelect, { matchesClientFilter, clientFilterFromParams, applyClientFilterParam } from "../components/ClientFilterSelect";
import { toast } from "sonner";
import QuickCompleteButton from "../components/QuickCompleteButton";
import ClientPropertyPicker from "../components/ClientPropertyPicker";
import { AssetPicker } from "../components/assets/AssetPicker";
import AccessNotesField from "../components/AccessNotesField";
import { useAuth } from "../contexts/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { JobCalendar } from "../components/JobCalendar";
import { JobWeekView } from "../components/JobWeekView";
import AddressAutocomplete from "../components/AddressAutocomplete";
import JobStartStatusBadge, { jobStartRowAccent } from "../components/JobStartStatusBadge";

import DateInput from "../components/DateInput";
const STATUS_MAP = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" },
};
const STATUS_ORDER = { scheduled: 0, in_progress: 1, completed: 2, cancelled: 3 };
const CATEGORIES = ["general", "plumbing", "electrical", "carpentry", "painting", "roofing", "landscaping", "cleaning", "hvac", "fire_safety", "security", "mould_eradication", "other"];
const PAGE_SIZE = 15;
const DONE_STATUSES = ["completed", "cancelled"];
const hasScope = (j) => !!(j.scope_of_works || "").trim();

export default function JobsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { byId: waitingById } = useClientWaiting();
  const [expandedParents, setExpandedParents] = useState([]);
  const toggleExpand = (id) => setExpandedParents(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const canBulk = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();
  const [teamFilter, setTeamFilter] = useState(() => {
    const t = searchParams.get("team");
    if (!t) return "all";
    return t === "unclaimed" ? "__unclaimed__" : t;
  });
  const [clientFilter, setClientFilterState] = useState(() => clientFilterFromParams(searchParams));
  const setClientFilter = (v) => { setClientFilterState(v); setSearchParams(applyClientFilterParam(searchParams, v), { replace: true }); };
  const [adminUsers, setAdminUsers] = useState([]);
  const [myWork, setMyWork] = useState(() => localStorage.getItem("fixli_my_work") === "1");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [operatives, setOperatives] = useState([]);
  const [forms, setForms] = useState([]);
  const [view, setView] = useState("list");

  // Sort & pagination
  const [sortKey, setSortKey] = useState("scheduled");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  // Bulk selection (admin) — assign / reschedule / status / delete
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [bulkDialog, setBulkDialog] = useState(null); // "assign" | "reschedule" | "status" | null
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkForm, setBulkForm] = useState({ vendor_id: "", operative_ids: [], start_date: "", start_time: "", status: "scheduled" });
  const [recurringOnly, setRecurringOnly] = useState(false);
  const [missingScope, setMissingScope] = useState(false);
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  // Compliance docs available for linking into the new job (admin picks
  // RAMS / Method Statement / COSHH / SDS at job-create time).
  const [complianceDocs, setComplianceDocs] = useState([]);

  const toggleSelectJob = (id) => setSelectedJobIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const [form, setForm] = useState({
    title: "", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "",
    requester_contact_id: "", requester_contact_name: "", requester_contact_color: "",
    property_id: "", add_to_portfolio: false, asset_id: "", asset_name: "",
    property_name: "", street_address: "", town_city: "", county: "", postcode: "", what3words: "",
    site_contact_name: "", site_contact_phone: "", site_contact_email: "",
    access_notes: "",
    category: "general", assigned_vendor_id: "", assigned_vendor_name: "",
    assigned_operative_id: "", assigned_operative_name: "",
    start_date: "", end_date: "", start_time: "", end_time: "", instructions: "", scope_of_works: "",
    attachments: [], job_form_ids: [], compliance_doc_ids: [], notice: { ...EMPTY_NOTICE },
  });
  const [allClients, setAllClients] = useState([]);
  const [selectedClientContacts, setSelectedClientContacts] = useState([]);
  const [workTemplates, setWorkTemplates] = useState([]);

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      params.set("limit", "1000");
      const { data } = await API.get(`/jobs?${params}`);
      setJobs(data.jobs);
      setTotal(data.total);
    } catch { toast.error("Failed to load jobs"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // Per-job compliance ack status — drives the red 🛡️ badge in the table.
  const jobIdsForBadges = jobs.map(j => j.id);
  const complianceByJob = useComplianceBadges(jobIdsForBadges);

  // Auto-open create dialog when arriving with ?new=1 (from QuickCreateMenu)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setShowCreate(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    API.get("/suppliers?limit=200").then(({ data }) => setVendors(Array.isArray(data) ? data : data.vendors || [])).catch(() => {});
    API.get("/in-house/team").then(({ data }) => setOperatives((data.team || []).filter(u => u.active !== false))).catch(() => {});
    API.get("/job-forms").then(({ data }) => setForms(data.forms || [])).catch(() => {});
    API.get("/clients?limit=500").then(({ data }) => setAllClients(data.clients || [])).catch(() => {});
    API.get("/work-templates?template_type=job").then(({ data }) => setWorkTemplates(data.templates || [])).catch(() => {});
    API.get("/checklist-templates").then(({ data }) => setChecklistTemplates((data.templates || []).filter(t => t.active !== false))).catch(() => {});
    // Compliance documents are surfaced inside the Job Create dialog so an
    // admin can attach the right RAMS / Method Statement upfront. The
    // operative is then forced to acknowledge each one before starting
    // the job (handled by JobCompliancePanel on the job detail page).
    API.get("/compliance-hub/documents").then(({ data }) => setComplianceDocs(data.documents || [])).catch(() => {});
  }, []);

  const applyTemplate = (templateId) => {
    if (!templateId || templateId === "__none__") return;
    const tpl = workTemplates.find(t => t.id === templateId);
    if (!tpl || !tpl.fields) return;
    setForm(prev => ({ ...prev, ...tpl.fields }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const handleSelectClient = (clientId) => {
    if (!clientId || clientId === "__none__") {
      setForm(prev => ({ ...prev, client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "" }));
      setSelectedClientContacts([]);
      return;
    }
    const client = allClients.find(c => c.id === clientId);
    if (client) {
      setForm(prev => ({
        ...prev,
        client_id: client.id,
        client_name: `${client.first_name} ${client.last_name}`,
        client_email: client.email || "",
        client_phone: client.phone || "",
        client_company: client.company_name || "",
        requester_contact_id: "",
        requester_contact_name: "",
        requester_contact_color: "",
      }));
      setSelectedClientContacts(client.contacts || []);
    }
  };

  const handleSelectRequester = (contactId) => {
    if (!contactId || contactId === "__none__") {
      setForm(prev => ({ ...prev, requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", requester_contact_email: "", requester_contact_phone: "" }));
      return;
    }
    const contact = selectedClientContacts.find(c => c.id === contactId);
    if (contact) {
      setForm(prev => ({ ...prev, requester_contact_id: contact.id, requester_contact_name: `${contact.first_name} ${contact.last_name}`, requester_contact_color: contact.color || "#3b82f6", requester_contact_email: contact.email || "", requester_contact_phone: contact.phone || "" }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { recurrence_frequency, compliance_doc_ids, ...rest } = form;
      const payload = {
        ...rest,
        recurrence: recurrence_frequency ? { enabled: true, frequency: recurrence_frequency } : null,
      };
      const { data } = await API.post("/jobs", payload);
      // Link selected compliance docs in a single round-trip — the operative
      // will be forced to acknowledge each one before starting work via the
      // JobCompliancePanel on the job detail page.
      if (Array.isArray(compliance_doc_ids) && compliance_doc_ids.length > 0) {
        try {
          await API.post(`/jobs/${data.id}/compliance-hub/link`, { doc_ids: compliance_doc_ids });
          toast.success(`Job ${data.reference} created · ${compliance_doc_ids.length} compliance doc${compliance_doc_ids.length === 1 ? "" : "s"} linked`);
        } catch (linkErr) {
          // Job is created; link is best-effort. Surface the failure but
          // don't roll back — admin can re-link from the doc detail page.
          toast.warning(`Job ${data.reference} created, but compliance doc linking failed: ${linkErr.response?.data?.detail || linkErr.message}`);
        }
      } else {
        toast.success(`Job ${data.reference} created`);
      }
      setShowCreate(false);
      setForm({ title: "", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "", requester_contact_id: "", requester_contact_name: "", requester_contact_color: "", property_name: "", street_address: "", town_city: "", county: "", postcode: "", what3words: "", site_contact_name: "", site_contact_phone: "", site_contact_email: "", access_notes: "", category: "general", assigned_vendor_id: "", assigned_vendor_name: "", start_date: "", start_time: "", end_time: "", instructions: "", scope_of_works: "", attachments: [], job_form_ids: [], compliance_doc_ids: [], recurrence_frequency: "", checklist_template_id: "", notice: { ...EMPTY_NOTICE } });
      setSelectedClientContacts([]);
      navigate(`/jobs/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create job"); }
    finally { setCreating(false); }
  };

  // Filter -> Sort -> Paginate
  const filtered = useMemo(() => {
    let list = jobs;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(j => j.title?.toLowerCase().includes(s) || j.reference?.toLowerCase().includes(s) || j.client_name?.toLowerCase().includes(s) || j.street_address?.toLowerCase().includes(s) || j.assigned_vendor_name?.toLowerCase().includes(s) || j.assigned_operative_name?.toLowerCase().includes(s));
    }
    if (teamFilter !== "all") {
      list = teamFilter === "__unclaimed__"
        ? list.filter(j => !j.actioned_by)
        : list.filter(j => j.actioned_by_name === teamFilter);
    }
    if (clientFilter !== "all") list = list.filter(j => matchesClientFilter(j, clientFilter));
    if (myWork) list = list.filter(j => j.actioned_by === String(user?._id));
    if (recurringOnly) list = list.filter(j => j.recurrence?.enabled);
    if (missingScope) list = list.filter(j => !hasScope(j) && (statusFilter !== "all" || !DONE_STATUSES.includes(j.status)));
    return list;
  }, [jobs, search, teamFilter, clientFilter, myWork, recurringOnly, missingScope, statusFilter, user]);

  const missingScopeCount = useMemo(() => jobs.filter(j => !hasScope(j) && !DONE_STATUSES.includes(j.status)).length, [jobs]);

  const uniqueActioners = useMemo(
    () => [...new Set(jobs.filter(j => j.actioned_by_name).map(j => j.actioned_by_name))].sort(),
    [jobs]
  );

  const uniqueAdmins = useMemo(() => adminUsers.reduce((acc, a) => {
    if (a.active !== false && !acc.some(x => x.name === a.name)) acc.push(a);
    return acc;
  }, []), [adminUsers]);

  const toggleMyWork = () => setMyWork(v => { const nv = !v; localStorage.setItem("fixli_my_work", nv ? "1" : "0"); return nv; });

  const reassignJob = async (j, admin) => {
    try {
      await API.post("/sla/reassign", { entity_type: "jobs", entity_id: j.id, user_id: admin ? String(admin._id) : "" });
      toast.success(admin ? (String(admin._id) === String(user?._id) ? `Claimed ${j.reference}` : `Handed to ${admin.name}`) : "Unclaimed");
      fetchJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reassign"); }
  };

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let av, bv;
      switch (sortKey) {
        case "reference": av = a.reference || ""; bv = b.reference || ""; break;
        case "client": av = a.client_name || ""; bv = b.client_name || ""; break;
        case "property": av = a.street_address || ""; bv = b.street_address || ""; break;
        case "vendor": av = a.assigned_vendor_name || ""; bv = b.assigned_vendor_name || ""; break;
        case "status": av = STATUS_ORDER[a.status] ?? 99; bv = STATUS_ORDER[b.status] ?? 99; return (av - bv) * dir;
        case "scheduled":
        default: av = a.schedule?.start_date || "9999"; bv = b.schedule?.start_date || "9999"; break;
      }
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, statusFilter, teamFilter, clientFilter, sortKey, sortDir, recurringOnly, missingScope]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const handleReschedule = async (jobId, newDate) => {
    try {
      await API.put(`/jobs/${jobId}`, { start_date: newDate });
      toast.success("Job rescheduled");
      fetchJobs();
    } catch { toast.error("Failed to reschedule"); }
  };

  const handleQuickCreate = (dateStr) => {
    setForm(prev => ({ ...prev, start_date: dateStr }));
    setShowCreate(true);
  };

  // ── Bulk actions ─────────────────────────────────────────────
  const toggleSelectAllJobs = () => {
    const pageIds = paginated.map(j => j.id);
    const allSelected = pageIds.every(id => selectedJobIds.includes(id));
    setSelectedJobIds(prev => allSelected ? prev.filter(id => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]);
  };

  const runBulk = async (endpoint, payload, successMsg) => {
    setBulkBusy(true);
    try {
      const { data } = await API.post(endpoint, { job_ids: selectedJobIds, ...payload });
      toast.success(data.message || successMsg);
      setSelectedJobIds([]);
      setBulkDialog(null);
      fetchJobs();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const handleBulkDelete = () => {
    if (!window.confirm(`Delete ${selectedJobIds.length} job(s)? This cannot be undone.`)) return;
    runBulk("/bulk/jobs/delete", {}, "Jobs deleted");
  };

  // Bulk complete with two-click confirm + 10 s Undo (reverts each job to its previous status)
  const [bulkCompleteConfirm, setBulkCompleteConfirm] = useState(false);
  useEffect(() => {
    if (!bulkCompleteConfirm) return;
    const t = setTimeout(() => setBulkCompleteConfirm(false), 4000);
    return () => clearTimeout(t);
  }, [bulkCompleteConfirm]);
  const openSelected = jobs.filter(j => selectedJobIds.includes(j.id) && !["completed", "cancelled"].includes(j.status));
  const handleBulkComplete = async () => {
    if (!bulkCompleteConfirm) { setBulkCompleteConfirm(true); return; }
    setBulkCompleteConfirm(false);
    if (openSelected.length === 0) { toast.info("Selected jobs are already completed or cancelled"); return; }
    const previous = openSelected.map(j => ({ id: j.id, status: j.status }));
    const undo = async () => {
      try {
        const groups = previous.reduce((acc, p) => { (acc[p.status] ||= []).push(p.id); return acc; }, {});
        await Promise.all(Object.entries(groups).map(([status, ids]) => API.post("/bulk/jobs/update-status", { job_ids: ids, status })));
        toast.success(`${previous.length} job(s) reverted`);
        fetchJobs();
      } catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
    };
    setBulkBusy(true);
    try {
      await API.post("/bulk/jobs/update-status", { job_ids: previous.map(p => p.id), status: "completed" });
      toast.success(`${previous.length} job(s) marked as completed`, {
        description: "Mis-click? You can undo for 10 seconds.", duration: 10000,
        action: { label: "Undo", onClick: undo },
      });
      setSelectedJobIds([]);
      fetchJobs();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk complete failed"); }
    finally { setBulkBusy(false); }
  };

  return (
    <PullToRefresh onRefresh={fetchJobs} className="max-w-6xl mx-auto">
    <div className="max-w-6xl mx-auto" data-testid="jobs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Jobs</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{(teamFilter !== "all" || clientFilter !== "all" || search || myWork || recurringOnly || missingScope) ? `${filtered.length} of ${total}` : `${total} total`}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border rounded-md overflow-hidden" data-testid="jobs-view-toggle">
            <button onClick={() => setView("list")} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="view-list-btn">
              <List size={14} /> List
            </button>
            <button onClick={() => setView("month")} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${view === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="view-month-btn">
              <CalendarBlank size={14} /> Month
            </button>
            <button onClick={() => setView("week")} className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="view-week-btn">
              <CalendarDots size={14} /> Week
            </button>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-job-btn">
            <Plus size={16} className="mr-1" /> New Job
          </Button>
        </div>
      </div>

      {view === "month" ? (
        <JobCalendar jobs={jobs} vendors={vendors} onReschedule={handleReschedule} onQuickCreate={handleQuickCreate} complianceByJob={complianceByJob} />
      ) : view === "week" ? (
        <JobWeekView jobs={jobs} onReschedule={handleReschedule} onQuickCreate={handleQuickCreate} complianceByJob={complianceByJob} />
      ) : (
      <>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search jobs..." className="pl-8 h-8 text-xs" data-testid="jobs-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <ClientFilterSelect rows={jobs} value={clientFilter} onChange={setClientFilter} className="w-[200px] h-8 text-xs" testid="jobs-client-filter" />
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="jobs-team-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Team Members</SelectItem>
            <SelectItem value="__unclaimed__">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-600 inline-block" /> Unclaimed</span>
            </SelectItem>
            {uniqueActioners.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant={myWork ? "default" : "outline"} className="h-8 text-xs" onClick={toggleMyWork} data-testid="jobs-my-work-toggle">
          <Hand size={13} className="mr-1" /> My Work
        </Button>
        <button
          onClick={() => setRecurringOnly(v => !v)}
          className={`flex items-center gap-1 px-2.5 h-8 rounded-md border text-xs font-medium transition-colors ${recurringOnly ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
          title="Show only recurring jobs"
          data-testid="jobs-recurring-filter"
        >
          <ArrowsClockwise size={13} /> Recurring
        </button>
        <button
          onClick={() => setMissingScope(v => !v)}
          className={`flex items-center gap-1 px-2.5 h-8 rounded-md border text-xs font-medium transition-colors ${missingScope ? "border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" : "border-border text-muted-foreground hover:border-amber-400/60"}`}
          title="Open jobs that still need a scope of works"
          data-testid="jobs-missing-scope-filter"
        >
          <ListChecks size={13} /> Missing scope{missingScopeCount ? <span className="tabular-nums" data-testid="jobs-missing-scope-count">({missingScopeCount})</span> : null}
        </button>
      </div>

      {/* Bulk action bar */}
      {canBulk && selectedJobIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-md border border-primary/40 bg-primary/5" data-testid="jobs-bulk-bar">
          <span className="text-xs font-semibold">{selectedJobIds.length} selected</span>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setBulkForm(f => ({ ...f, vendor_id: "", operative_ids: [] })); setBulkDialog("assign"); }} data-testid="bulk-assign-btn">
            <UserPlus size={12} className="mr-1" /> Assign
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setBulkForm(f => ({ ...f, start_date: "", start_time: "" })); setBulkDialog("reschedule"); }} data-testid="bulk-reschedule-btn">
            <CalendarCheck size={12} className="mr-1" /> Reschedule
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => { setBulkForm(f => ({ ...f, status: "scheduled" })); setBulkDialog("status"); }} data-testid="bulk-status-btn">
            <CheckSquare size={12} className="mr-1" /> Status
          </Button>
          <Button size="sm" disabled={bulkBusy} onClick={handleBulkComplete} variant={bulkCompleteConfirm ? "default" : "outline"}
            className={`h-7 text-[11px] ${bulkCompleteConfirm ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/30"}`}
            title={bulkCompleteConfirm ? "Click again to confirm" : "Mark the selected jobs as completed"} data-testid="bulk-complete-btn">
            <CheckCircle size={12} className="mr-1" weight={bulkCompleteConfirm ? "fill" : "regular"} />
            {bulkCompleteConfirm ? `Complete ${openSelected.length} — confirm?` : "Complete"}
          </Button>
          {canDelete && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={handleBulkDelete} disabled={bulkBusy} data-testid="bulk-delete-btn">
              <Trash size={12} className="mr-1" /> Delete
            </Button>
          )}
          <button className="text-[11px] text-muted-foreground hover:underline ml-auto" onClick={() => setSelectedJobIds([])} data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="border border-border rounded-md bg-card overflow-x-auto" data-testid="jobs-list">
        {loading ? (
          <div className="p-3"><CardListSkeleton rows={6} showHeader={false} /></div>
        ) : sorted.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No jobs found</div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                {canBulk && (
                  <th className="px-3 py-2 w-8">
                    <button onClick={toggleSelectAllJobs} className="text-muted-foreground hover:text-primary align-middle" title="Select all on page" data-testid="jobs-select-all">
                      {paginated.length > 0 && paginated.every(j => selectedJobIds.includes(j.id)) ? <CheckSquare size={15} weight="fill" className="text-primary" /> : <Square size={15} />}
                    </button>
                  </th>
                )}
                <SortHeader label="Job No." k="reference" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                <SortHeader label="Client" k="client" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                <SortHeader label="Property" k="property" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} className="hidden md:table-cell" />
                <SortHeader label="Assigned To" k="vendor" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} className="hidden lg:table-cell" />
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground hidden lg:table-cell">Team</th>
                <SortHeader label="Scheduled" k="scheduled" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground hidden md:table-cell">Start Status</th>
                <SortHeader label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} align="right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginated.map(j => {
                const st = STATUS_MAP[j.status] || STATUS_MAP.scheduled;
                const addr = [j.street_address, j.town_city, j.postcode].filter(Boolean).join(", ");
                const schedDate = j.schedule?.start_date ? new Date(j.schedule.start_date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";
                const schedTime = j.schedule?.start_time ? `${j.schedule.start_time}${j.schedule.end_time ? " — " + j.schedule.end_time : ""}` : "";
                const isParent = !!j.sub_job_summary?.total;
                const expanded = isParent && expandedParents.includes(j.id);
                const colCount = (canBulk ? 1 : 0) + 8;
                return (
                  <Fragment key={j.id}>
                  <tr className={`row-hover cursor-pointer ${jobStartRowAccent(j)}`} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}>
                    {canBulk && (
                      <td className="px-3 py-3 w-8" onClick={e => e.stopPropagation()}>
                        <button onClick={() => toggleSelectJob(j.id)} className="text-muted-foreground hover:text-primary align-middle" data-testid={`job-select-${j.id}`}>
                          {selectedJobIds.includes(j.id) ? <CheckSquare size={15} weight="fill" className="text-primary" /> : <Square size={15} />}
                        </button>
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <p className="text-xs font-mono font-semibold text-primary flex items-center gap-1">
                        {j.reference}
                        {j.recurrence?.enabled && <ArrowsClockwise size={11} className="text-emerald-600" title={`Recurring ${j.recurrence.frequency || ""}`} data-testid={`job-recurring-badge-${j.id}`} />}
                        <ComplianceBadge summary={complianceByJob[j.id]} jobId={j.id} />
                        <ClientWaitingPill item={waitingById[j.id]} testId={`job-waiting-${j.id}`} />
                        {j.client_sign_off && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap" title={`Signed off by ${j.client_sign_off.typed_name}`} data-testid={`job-signed-off-${j.id}`}>✓ Signed off</span>}
                        {isParent && (
                          <button type="button" onClick={e => { e.stopPropagation(); toggleExpand(j.id); }} className="inline-flex items-center gap-0.5" title={expanded ? "Hide sub-jobs" : "Show sub-jobs"} data-testid={`job-subjobs-toggle-${j.id}`}>
                            <SubJobChip summary={j.sub_job_summary} testid={`job-subjobs-chip-${j.id}`} />
                            {expanded ? <CaretUp size={11} className="text-violet-600" /> : <CaretDown size={11} className="text-violet-600" />}
                          </button>
                        )}
                      </p>
                      <p className="text-xs text-foreground truncate max-w-[180px] mt-0.5">{j.title}</p>
                      {j.created_by_name && <p className="text-[10px] text-muted-foreground truncate max-w-[180px]" data-testid={`job-added-by-${j.id}`}>Added by {j.created_by_name}</p>}
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-xs font-medium truncate max-w-[150px]">{j.client_name || "—"}</p>
                      {j.client_company && <p className="text-[10px] text-muted-foreground truncate max-w-[150px]">{j.client_company}</p>}
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <p className="text-xs text-muted-foreground truncate max-w-[200px]">{addr || "—"}</p>
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell">
                      <div className="space-y-0.5">
                        {isParent ? (
                          <span className="text-xs text-violet-700 dark:text-violet-300 italic">Per sub-job</span>
                        ) : j.assigned_vendor_name ? (
                          <span className="text-xs flex items-center gap-1 truncate max-w-[180px]" title={`Supplier: ${j.assigned_vendor_name}`}><Wrench size={12} className="text-muted-foreground shrink-0" /> {j.assigned_vendor_name}</span>
                        ) : !j.assigned_operative_name ? (
                          <span className="text-xs text-muted-foreground/50">Unassigned</span>
                        ) : null}
                        {j.assigned_operative_name && (
                          <span className="text-xs flex items-center gap-1 truncate max-w-[180px]" title={`In-House: ${j.assigned_operative_name}`} data-testid={`job-operative-chip-${j.id}`}><User size={12} className="text-primary shrink-0" /> <span className="truncate">{j.assigned_operative_name}</span></span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell" onClick={e => e.stopPropagation()}>
                      {!j.actioned_by ? (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30" onClick={() => reassignJob(j, { _id: user?._id, name: user?.name })} data-testid={`job-claim-${j.id}`}>
                          <Hand size={11} className="mr-0.5" /> Claim
                        </Button>
                      ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" title={`Claimed by ${j.actioned_by_name} — hand over`} data-testid={`job-handover-${j.id}`}>
                              {(j.actioned_by_name || "").split(" ")[0]} <UserSwitch size={11} className="ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {uniqueAdmins.filter(a => String(a._id) !== j.actioned_by).map(a => (
                              <DropdownMenuItem key={a._id} onClick={() => reassignJob(j, a)} data-testid={`job-handover-to-${j.id}-${a._id}`}>Hand to {a.name}</DropdownMenuItem>
                            ))}
                            <DropdownMenuItem className="text-red-600" onClick={() => reassignJob(j, null)} data-testid={`job-unclaim-${j.id}`}>Unclaim</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {schedDate ? (
                        <div>
                          <p className="text-xs">{schedDate}</p>
                          {schedTime && <p className="text-[10px] text-muted-foreground">{schedTime}</p>}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">Not set</span>
                      )}
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell" data-testid={`job-sla-cell-${j.id}`}>
                      <JobStartStatusBadge job={j} />
                    </td>
                    <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium inline-block ${st.color}`}>{st.label}</span>
                        {canBulk && !isParent && <QuickCompleteButton entity="job" id={j.id} reference={j.reference} status={j.status} onDone={fetchJobs} testId={`job-complete-${j.id}`} clientPoNumber={j.client_po_number} poRequired={!!j.po_required} />}
                      </div>
                    </td>
                  </tr>
                  {expanded && <SubJobsExpandRows parentId={j.id} colSpan={colCount} />}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3" data-testid="jobs-pagination">
          <p className="text-[10px] text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
              <CaretLeft size={12} />
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map(p => (
              <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className="h-7 w-7 p-0 text-[10px]" onClick={() => setPage(p)} data-testid={`page-${p}`}>
                {p}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next">
              <CaretRight size={12} />
            </Button>
          </div>
        </div>
      )}
      </>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="create-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">New Job</DialogTitle>
            <DialogDescription>Create a new job and assign a supplier.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {workTemplates.length > 0 && (
              <div className="flex items-center gap-2 p-2.5 bg-muted/30 rounded-md border border-dashed border-border" data-testid="job-template-selector">
                <Bookmark size={14} className="text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Apply Template:</span>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="job-apply-template"><SelectValue placeholder="Select a template to pre-fill..." /></SelectTrigger>
                  <SelectContent>
                    {workTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Job Title *</Label>
              <AiBriefButton
                workType="job"
                category={form.category}
                propertyAddress={form.street_address}
                onApply={(brief) => setForm(prev => ({
                  ...prev,
                  title: brief.title || prev.title,
                  instructions: brief.description || prev.instructions,
                  category: brief.suggested_category || prev.category,
                }))}
              />
            </div>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className="mt-1" data-testid="job-title-input" />
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">CLIENT DETAILS</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Client</Label>
                  <Select value={form.client_id || "__none__"} onValueChange={handleSelectClient}>
                    <SelectTrigger className="mt-1" data-testid="job-client-select"><SelectValue placeholder="Select a client..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No client selected</SelectItem>
                      {allClients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.company_name ? `${c.company_name} — ${c.first_name} ${c.last_name}` : `${c.first_name} ${c.last_name}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {form.client_id && selectedClientContacts.length > 0 && (
                  <div className="col-span-2">
                    <Label>Requester Contact</Label>
                    <Select value={form.requester_contact_id || "__none__"} onValueChange={handleSelectRequester}>
                      <SelectTrigger className="mt-1" data-testid="job-requester-select"><SelectValue placeholder="Tag a sub-contact as requester..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No specific requester</SelectItem>
                        {selectedClientContacts.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full shrink-0 inline-block" style={{ backgroundColor: c.color || "#3b82f6" }} />
                              {c.first_name} {c.last_name}{c.job_role ? ` (${c.job_role})` : ""}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.client_id && (
                  <div className="col-span-2 grid grid-cols-2 gap-3 border border-border rounded-md p-3 bg-muted/20">
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Name</Label><p className="text-sm font-medium mt-0.5">{form.client_name || "—"}</p></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Company</Label><p className="text-sm font-medium mt-0.5">{form.client_company || "—"}</p></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email</Label><p className="text-sm font-medium mt-0.5">{form.client_email || "—"}</p></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Phone</Label><p className="text-sm font-medium mt-0.5">{form.client_phone || "—"}</p></div>
                    {form.requester_contact_name && (
                      <div className="col-span-2"><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client Requester</Label>
                        <span className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: form.requester_contact_color || "#3b82f6" }} data-testid="job-requester-tag">{form.requester_contact_name}</span>
                      </div>
                    )}
                  </div>
                )}
                {!form.client_id && (
                  <>
                    <div><Label>Name</Label><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} className="mt-1" placeholder="John Smith" data-testid="job-client-name" /></div>
                    <div><Label>Company</Label><Input value={form.client_company} onChange={e => setForm({ ...form, client_company: e.target.value })} className="mt-1" placeholder="Smith Properties Ltd" /></div>
                    <div><Label>Email</Label><Input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} className="mt-1" placeholder="john@smith.com" /></div>
                    <div><Label>Phone</Label><Input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} className="mt-1" placeholder="+44 7..." /></div>
                  </>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">PROPERTY DETAILS</p>
              <div className="mb-3">
                <ClientPropertyPicker clientId={form.client_id} clientLabel={allClients.find(c => c.id === form.client_id)?.company_name || allClients.find(c => c.id === form.client_id)?.first_name}
                  propertyId={form.property_id} addToPortfolio={form.add_to_portfolio} onToggleAdd={(v) => setForm(f => ({ ...f, add_to_portfolio: v }))}
                  canAdd={!!(form.street_address && form.postcode)} testid="job-client-property"
                  onPick={(p) => setForm(f => p ? ({ ...f, property_id: p.id, property_name: p.name || f.property_name, street_address: p.address_line || "", town_city: p.town_city || "", county: p.county || "", postcode: p.postcode || "", what3words: p.what3words || "", site_contact_name: p.site_contact_name || f.site_contact_name, site_contact_phone: p.site_contact_phone || f.site_contact_phone, site_contact_email: p.site_contact_email || f.site_contact_email, access_notes: p.access_notes || f.access_notes, add_to_portfolio: false }) : ({ ...f, property_id: "" }))} />
              </div>
              <AddressAutocomplete
                label="Search address"
                placeholder="Start typing postcode or address..."
                onSelect={(a) => setForm(f => ({
                  ...f,
                  street_address: a.street_address || f.street_address,
                  town_city: a.town_city || f.town_city,
                  county: a.county || f.county,
                  postcode: a.postcode || f.postcode,
                  latitude: a.latitude || null,
                  longitude: a.longitude || null,
                }))}
                className="mb-3"
                data-testid="job-address-autocomplete"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Property Name</Label><Input value={form.property_name} onChange={e => setForm({ ...form, property_name: e.target.value })} placeholder="Optional" className="mt-1" /></div>
                <AssetPicker value={form.asset_id} propertyId={form.property_id} clientId={form.client_id} onChange={(a) => setForm(f => ({ ...f, asset_id: a?.id || "", asset_name: a?.name || "" }))} />
                <div className="col-span-2"><Label>Street Address *</Label><Input value={form.street_address} onChange={e => setForm({ ...form, street_address: e.target.value })} required className="mt-1" data-testid="job-street" /></div>
                <div><Label>Town / City *</Label><Input value={form.town_city} onChange={e => setForm({ ...form, town_city: e.target.value })} required className="mt-1" data-testid="job-town" /></div>
                <div><Label>County</Label><Input value={form.county} onChange={e => setForm({ ...form, county: e.target.value })} className="mt-1" /></div>
                <div><Label>Postcode *</Label><Input value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })} required className="mt-1" data-testid="job-postcode" /></div>
                <div><Label>what3words <span className="text-[10px] text-muted-foreground">(optional)</span></Label><Input value={form.what3words} onChange={e => setForm({ ...form, what3words: e.target.value })} placeholder="///filled.count.soap" className="mt-1 font-mono" data-testid="job-what3words" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">SITE / PROPERTY CONTACT</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Site Contact</Label><Input value={form.site_contact_name} onChange={e => setForm({ ...form, site_contact_name: e.target.value })} placeholder="E.G Tenant" className="mt-1" data-testid="job-site-contact-name" /></div>
                <div><Label>Phone</Label><Input value={form.site_contact_phone} onChange={e => setForm({ ...form, site_contact_phone: e.target.value })} placeholder="+44 7..." className="mt-1" data-testid="job-site-contact-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={form.site_contact_email} onChange={e => setForm({ ...form, site_contact_email: e.target.value })} placeholder="tenant@email.com" className="mt-1" data-testid="job-site-contact-email" /></div>
              </div>
            </div>
            <AccessNotesField value={form.access_notes} onChange={(v) => setForm(f => ({ ...f, access_notes: v }))} propertyLinked={!!form.property_id} testid="job-access-notes" />
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Assign Supplier</Label>
                <AiSmartAssignButton
                  category={form.category}
                  title={form.title}
                  description={form.instructions}
                  area={form.town_city || form.county}
                  onSelect={(rec) => setForm(prev => ({ ...prev, assigned_vendor_id: rec.vendor_id, assigned_vendor_name: rec.vendor_name }))}
                />
              </div>
              <Select value={form.assigned_vendor_id || "none"} onValueChange={v => {
                if (v === "none") { setForm({ ...form, assigned_vendor_id: "", assigned_vendor_name: "" }); return; }
                const vnd = vendors.find(x => x.id === v);
                setForm({ ...form, assigned_vendor_id: v, assigned_vendor_name: vnd?.name || vnd?.company_name || "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="job-vendor-select"><SelectValue placeholder="Select supplier..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No supplier assigned</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name || v.company_name} — {v.trade || "General"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assign In-House Operative</Label>
              <Select value={form.assigned_operative_id || "none"} onValueChange={v => {
                if (v === "none") { setForm({ ...form, assigned_operative_id: "", assigned_operative_name: "" }); return; }
                const op = operatives.find(x => x._id === v);
                setForm({ ...form, assigned_operative_id: v, assigned_operative_name: op?.name || op?.email || "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="job-operative-select"><SelectValue placeholder="Select operative..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No operative assigned</SelectItem>
                  {operatives.length === 0 ? (
                    <div className="px-2 py-3 text-xs text-muted-foreground text-center">No in-house operatives yet. Add them in Team &rsaquo; In-House.</div>
                  ) : operatives.map(op => (
                    <SelectItem key={op._id} value={op._id} data-testid={`job-operative-option-${op._id}`}>
                      {op.name || op.email}{op.trade ? ` — ${op.trade}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Optionally assign an in-house team member alongside or instead of a supplier.</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">SCHEDULE</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Start Date</Label><DateInput value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="job-start-date" /></div>
                <div><Label>End Date <span className="text-[10px] text-muted-foreground">(optional, multi-day)</span></Label><DateInput value={form.end_date || ""} min={form.start_date || undefined} onChange={e => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="job-end-date" /></div>
                <div><Label>Start Time</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="mt-1" data-testid="job-start-time" /></div>
                <div><Label>End Time</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="job-end-time" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">REPEAT &amp; CHECKLIST</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1"><ArrowsClockwise size={12} /> Recurrence</Label>
                  <Select value={form.recurrence_frequency || "none"} onValueChange={v => setForm({ ...form, recurrence_frequency: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="job-recurrence-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Does not repeat</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.recurrence_frequency && <p className="text-[10px] text-muted-foreground mt-1">A new copy is auto-created after each completion.</p>}
                </div>
                <div>
                  <Label className="flex items-center gap-1"><ListChecks size={12} /> Checklist</Label>
                  <Select value={form.checklist_template_id || "auto"} onValueChange={v => setForm({ ...form, checklist_template_id: v === "auto" ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid="job-checklist-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (match category)</SelectItem>
                      {checklistTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Manage in <Link to="/settings/checklists" className="underline" onClick={() => setShowCreate(false)}>Job Checklists</Link>.</p>
                </div>
              </div>
            </div>
            <div>
              <Label>Instructions</Label>
              <Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={3} className="mt-1" placeholder="Any special instructions for the supplier..." data-testid="job-instructions" />
            </div>
            <ScopeOfWorksField value={form.scope_of_works} onChange={(v) => setForm(f => ({ ...f, scope_of_works: v }))} testid="job-scope-of-works"
              context={{ entity: "job", title: form.title, description: form.instructions, client_name: form.client_company || form.client_name, property_address: [form.street_address, form.town_city, form.postcode].filter(Boolean).join(", ") }} />
            <NoticeEditor value={form.notice} onChange={(n) => setForm({ ...form, notice: n })} clientToggle aiContext={{ entity: "job", title: form.title, description: form.description }} testid="job-notice" compact />
            <div>
              <Label>Supporting Images / Files</Label>
              <FileDropZone files={form.attachments} onFilesChange={files => setForm({ ...form, attachments: files })} folder="jobs" accept="all" maxFiles={10} className="mt-1" />
            </div>
            {forms.length > 0 && (
              <div>
                <Label>Job Forms</Label>
                <p className="text-[10px] text-muted-foreground mb-1">Select forms the supplier needs to complete for this job</p>
                <div className="space-y-1.5 mt-1 max-h-32 overflow-y-auto border border-border rounded-md p-2">
                  {forms.map(f => (
                    <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 px-2 py-1 rounded">
                      <input type="checkbox" checked={form.job_form_ids.includes(f.id)} onChange={e => {
                        if (e.target.checked) setForm(prev => ({ ...prev, job_form_ids: [...prev.job_form_ids, f.id] }));
                        else setForm(prev => ({ ...prev, job_form_ids: prev.job_form_ids.filter(id => id !== f.id) }));
                      }} className="rounded" />
                      <span>{f.title}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{f.fields?.length || 0} fields</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {complianceDocs.length > 0 && (
              <div>
                <Label>Compliance Documents</Label>
                <p className="text-[10px] text-muted-foreground mb-1">
                  Operatives must acknowledge each linked document before they can start the job.
                </p>
                <ComplianceDocsPicker
                  docs={complianceDocs}
                  selectedIds={form.compliance_doc_ids}
                  onChange={(ids) => setForm(prev => ({ ...prev, compliance_doc_ids: ids }))}
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="create-job-submit">{creating ? "Creating..." : "Create Job"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {/* Bulk dialogs */}
      <Dialog open={bulkDialog === "assign"} onOpenChange={(o) => !o && setBulkDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign {selectedJobIds.length} job(s)</DialogTitle>
            <DialogDescription>Choose a supplier and/or in-house operative. Assignees get an in-app notification.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Supplier</Label>
              <Select value={bulkForm.vendor_id || "none"} onValueChange={v => setBulkForm(f => ({ ...f, vendor_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="bulk-vendor-select"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No change</SelectItem>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name || v.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>In-House Team <span className="text-muted-foreground font-normal">(tick one or more — first ticked is lead)</span></Label>
              <div className="mt-1 border border-border rounded-md divide-y divide-border max-h-48 overflow-y-auto" data-testid="bulk-operative-list">
                {operatives.length === 0 && <p className="text-xs text-muted-foreground italic px-3 py-2">No in-house operatives yet.</p>}
                {operatives.map(op => {
                  const on = (bulkForm.operative_ids || []).includes(op._id);
                  return (
                    <label key={op._id} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted/50">
                      <input type="checkbox" checked={on} data-testid={`bulk-operative-${op._id}`}
                        onChange={() => setBulkForm(f => ({ ...f, operative_ids: on ? (f.operative_ids || []).filter(x => x !== op._id) : [...(f.operative_ids || []), op._id] }))} />
                      <span>{op.name || op.email}</span>
                      {(bulkForm.operative_ids || [])[0] === op._id && <span className="text-[10px] text-amber-600 font-medium">lead</span>}
                    </label>
                  );
                })}
              </div>
              {(bulkForm.operative_ids || []).length > 0 && <p className="text-[11px] text-muted-foreground mt-1">{bulkForm.operative_ids.length} operative(s) will be set on every selected job.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(null)}>Cancel</Button>
            <Button disabled={bulkBusy || (!bulkForm.vendor_id && !(bulkForm.operative_ids || []).length)} onClick={() => runBulk("/bulk/jobs/assign", { vendor_id: bulkForm.vendor_id || null, operative_ids: bulkForm.operative_ids || [] }, "Jobs assigned")} data-testid="bulk-assign-confirm">
              {bulkBusy ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDialog === "reschedule"} onOpenChange={(o) => !o && setBulkDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="bulk-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule {selectedJobIds.length} job(s)</DialogTitle>
            <DialogDescription>All selected jobs move to this start date.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>New Start Date *</Label><DateInput value={bulkForm.start_date} onChange={e => setBulkForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1" data-testid="bulk-date-input" /></div>
            <div><Label>Start Time</Label><Input type="time" value={bulkForm.start_time} onChange={e => setBulkForm(f => ({ ...f, start_time: e.target.value }))} className="mt-1" data-testid="bulk-time-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(null)}>Cancel</Button>
            <Button disabled={bulkBusy || !bulkForm.start_date} onClick={() => runBulk("/bulk/jobs/reschedule", { start_date: bulkForm.start_date, start_time: bulkForm.start_time }, "Jobs rescheduled")} data-testid="bulk-reschedule-confirm">
              {bulkBusy ? "Rescheduling…" : "Reschedule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDialog === "status"} onOpenChange={(o) => !o && setBulkDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="bulk-status-dialog">
          <DialogHeader>
            <DialogTitle>Change status of {selectedJobIds.length} job(s)</DialogTitle>
          </DialogHeader>
          <div>
            <Label>New Status</Label>
            <Select value={bulkForm.status} onValueChange={v => setBulkForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="mt-1" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(null)}>Cancel</Button>
            <Button disabled={bulkBusy} onClick={() => runBulk("/bulk/jobs/update-status", { status: bulkForm.status }, "Status updated")} data-testid="bulk-status-confirm">
              {bulkBusy ? "Updating…" : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PullToRefresh>
  );
}
